/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { SmartLink as Link } from '@/utils/smartLink';
import root from 'window-or-global';
import React from 'react';
import { ProductInfoItemContainer } from '../ProductInfoItemContainer';
import styles from './styles.module.scss';
import { ReviewStats } from '@/features/customReviews/components/ReviewStats/ReviewStats';
import { useFeatureFlag } from '@/features/globalConfig';
import { roundValueToPrecision } from '@/components/Rating/utils';
import type { ReviewStatistic } from '../../../customReviews/interface/types';
import { type ProductAnalyticsData } from '../../interface';
import { Actionable } from '@az/starc-ui';
import { useRouter } from 'next/router';

type ReviewAnalyticsData = {
  rating: number;
  reviewCount: number;
  productAnalyticsData?: ProductAnalyticsData;
};

const trackReviewLinkShelf = ({
  rating,
  reviewCount,
  productAnalyticsData,
}: ReviewAnalyticsData) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data ?? {};

  clickTrack({
    eventType: 'reviewsLinkInteractionShelf',
    customLinkName: `reviewsInteraction - ${pageType}`,
    pageName,
    pageType,
    siteSection,
    productAverageRating: rating,
    productReviewCount: reviewCount,
    ...productAnalyticsData,
  });
};

type Props = {
  skuNumber: number;
  url: string;
  onProductClick?: () => void;
  reviewStatistic?: ReviewStatistic;
  productAnalyticsData?: ProductAnalyticsData;
  reviewsPending?: boolean;
};

export const ProductReviews = ({
  skuNumber,
  reviewStatistic,
  reviewsPending,
  productAnalyticsData,
  url,
  onProductClick,
}: Props) => {
  const reviewCount = Number(reviewStatistic?.totalReviewCount || '0');
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const router = useRouter();

  /* analytics Code start : review clickTrack */
  const handleReviewClick = () => {
    // Handle the product click first
    onProductClick?.();

    // Prepare analytics data
    const averageRating = reviewStatistic?.averageOverallRating || '0';
    const rating = roundValueToPrecision(Number(averageRating), 0.1);

    trackReviewLinkShelf({
      rating,
      reviewCount,
      productAnalyticsData,
    });
    /* analytics Code end : review clickTrack */
    void router.push(`${url}`);
  };

  const hasReviews =
    reviewStatistic && Number(reviewStatistic.totalReviewCount) > 0 && !reviewsPending;

  if (isCustomReviewsEnabled && (hasReviews || reviewsPending)) {
    return (
      <ProductInfoItemContainer
        data-testid="reviews-container"
        className={cx(styles.reviewsContainer, {
          [styles.customReviewsContainer]: isCustomReviewsEnabled,
        })}
      >
        {reviewStatistic && !reviewsPending ? (
          <Actionable onClick={() => handleReviewClick()} aria-hidden="true" tabIndex={-1}>
            <ReviewStats reviewStatistic={reviewStatistic} />
          </Actionable>
        ) : null}
      </ProductInfoItemContainer>
    );
  }

  if (!isCustomReviewsEnabled) {
    return (
      <ProductInfoItemContainer data-testid="reviews-container" className={styles.reviewsContainer}>
        <Link
          to={`${url}#bv_PDPReviews`}
          scroll={false}
          onClick={handleReviewClick}
          aria-hidden="true"
          tabIndex={-1}
        >
          <div
            data-bv-seo="false"
            data-bv-show="inline_rating"
            data-bv-product-id={skuNumber}
            className={`${styles.reviews}`}
            id={`inlineRatingsBV-${skuNumber}`}
          />
        </Link>
      </ProductInfoItemContainer>
    );
  }

  return null;
};
